import { select, useDispatch } from '@ps/web-core-store';
import { generatePath } from '@ps/web-core-utils';
import * as Sentry from '@sentry/react';
import type React from 'react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

const getCashGamesPathBasedOnSpade = (navigationPathName: string, cashGamesAliases: string) => {
  switch (navigationPathName) {
    case 'omaha':
    case 'holdem':
      return generatePath(cashGamesAliases, { view: 'simple', tab: navigationPathName });
    case 'all-in':
      return generatePath(cashGamesAliases, { view: 'all-in' });
    default:
      return generatePath(cashGamesAliases, { view: 'simple', tab: 'holdem' });
  }
};

const SpadeEmbeddedAPIProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const history = useHistory();
  const { modal } = useDispatch();
  const { idpToken, installId } = useSelector(select.session.get);
  const { pokerCashGames = '' } = useSelector(select.cms.getAliases);
  const isAuthenticated = useSelector(select.session.isAuthenticated);

  useEffect(() => {
    const subscriptions: ({ off: () => void } | undefined)[] = [];

    try {
      const loginSubscription = window.Spade?.api?.on('login', () => {
        if (!isAuthenticated) modal.enqueue({ path: '/auth/login' });
      });
      const navigationSubscription = window.Spade?.api?.on('navigation', (data) => {
        const path = getCashGamesPathBasedOnSpade(data.path, pokerCashGames);
        history.replace(path);
      });

      subscriptions.push(loginSubscription, navigationSubscription);
    } catch (error) {
      Sentry.captureException(error);
    }
    return () => {
      subscriptions.forEach((subscription) => subscription?.off());
    };
  }, [modal, isAuthenticated, pokerCashGames, history]);

  useEffect(() => {
    if (idpToken) {
      try {
        window.Spade?.api?.setToken({ token: idpToken });
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  }, [idpToken]);

  useEffect(() => {
    if (installId) {
      try {
        window.Spade?.api?.setInstallId({ installId });
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  }, [installId]);

  return children;
};

export default SpadeEmbeddedAPIProvider;
